import { useEffect } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Single in-memory fetch promise so multiple component mounts don't refetch.
let _overridesPromise = null;
function fetchOverrides() {
  if (!_overridesPromise) {
    _overridesPromise = axios
      .get(`${API}/seo-public/image-alts-bulk`)
      .then((r) => r.data || [])
      .catch(() => []);
  }
  return _overridesPromise;
}

// Normalise an image src for matching:
//  - resolve to absolute URL when possible
//  - lowercase
//  - strip query string and trailing slash
function normaliseSrc(raw) {
  if (!raw) return "";
  try {
    const u = new URL(raw, window.location.origin);
    return (u.origin + u.pathname).toLowerCase().replace(/\/+$/, "");
  } catch (_e) {
    return String(raw).toLowerCase().split("?")[0].replace(/\/+$/, "");
  }
}

// Decide whether a configured override src matches a given live <img> src.
// Match by exact normalised URL OR by trailing-path suffix (so SEO editors can
// configure either "/static/.../hero.jpg" or the full CDN URL and it still hits).
function srcMatches(configuredSrc, imgSrc) {
  if (!configuredSrc || !imgSrc) return false;
  const c = normaliseSrc(configuredSrc);
  const i = normaliseSrc(imgSrc);
  if (!c || !i) return false;
  if (c === i) return true;
  // Suffix match — allows /path-style overrides to hit absolute CDN URLs
  if (i.endsWith(c) || c.endsWith(i)) return true;
  // Filename-only fallback (last path segment)
  const cFile = c.split("/").pop();
  const iFile = i.split("/").pop();
  if (cFile && cFile === iFile && cFile.length > 6) return true;
  return false;
}

function applyOverrides(overrides) {
  if (!overrides || !overrides.length) return;
  const imgs = document.querySelectorAll("img");
  imgs.forEach((img) => {
    const src = img.currentSrc || img.src || img.getAttribute("data-src") || "";
    overrides.forEach((ov) => {
      if (!srcMatches(ov.image_src, src)) return;
      if (ov.alt_text && img.getAttribute("alt") !== ov.alt_text) {
        img.setAttribute("alt", ov.alt_text);
      }
      if (ov.title_text && img.getAttribute("title") !== ov.title_text) {
        img.setAttribute("title", ov.title_text);
      }
    });
  });
}

/**
 * Walks every <img> on the live page and applies Speicher-configured alt + title
 * overrides via a MutationObserver. Runs once per app mount.
 */
export default function useImageAttributes() {
  useEffect(() => {
    let cancelled = false;
    let observer = null;
    fetchOverrides().then((overrides) => {
      if (cancelled || !overrides.length) return;
      applyOverrides(overrides);
      // Reapply on later route changes / SPA renders / lazy-loaded images.
      observer = new MutationObserver(() => applyOverrides(overrides));
      observer.observe(document.body, { childList: true, subtree: true, attributes: true, attributeFilter: ["src"] });
    });
    return () => {
      cancelled = true;
      if (observer) observer.disconnect();
    };
  }, []);
}
